// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useEffect, useState } from "react";
import { NumberField } from "./NumberField";
import { RegionSelector } from "./RegionSelector";
import { CitationField } from "./CitationField";
import type { VaccineInputs, ComplicationCategory } from "../lib/models";
import { DEFAULT_INPUTS, resolveEffectiveTimeHorizon, canResolveLifetimeHorizon } from "../lib/models";
import {
  BACKGROUND_UTILITY_SOURCE_OPTIONS,
  findBackgroundUtilitySource,
} from "../../../lib/backgroundUtilitySourceCatalog";
import type { PopulationMode } from "../../../data/regions";
import { getNationalPopulation } from "../../../data/regions";

interface ModelInputsCardProps {
  inputs: VaccineInputs;
  isCollapsed: boolean;
  populationMode: PopulationMode;
  nationalPopulation: number;
  nationalCoverage: number;
  actions: {
    toggle: () => void;
    reset: () => void;
    changeRegions: (regions: string[]) => void;
    updateInput: (patch: Partial<VaccineInputs>) => void;
    changePopulationMode: (mode: PopulationMode) => void;
    changeNationalPopulation: (value: number) => void;
    changeNationalCoverage: (value: number) => void;
  };
  errors?: Record<string, string>;
}

export const ModelInputsCard: React.FC<ModelInputsCardProps> = ({
  inputs,
  isCollapsed,
  populationMode,
  nationalPopulation,
  nationalCoverage,
  actions,
  errors = {},
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    populationDemographics: true,
    diseaseEpidemiology: true,
    vaccineCharacteristics: true,
    adverseEvents: true,
    economicParameters: true,
    complications: true,
    multiYearAnalysis: false, // Default collapsed
    productivityCosts: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Effective horizon respects horizonMode='lifetime' (auto-derives from starting age
  // + mortality-source terminal age). All gating/length logic below uses this.
  const effectiveHorizon = resolveEffectiveTimeHorizon(inputs);
  const canUseLifetime = canResolveLifetimeHorizon(inputs);
  const horizonMode = inputs.horizonMode ?? 'manual';

  // Keep veSchedule length aligned with timeHorizonYears when in schedule mode.
  // Pad with the last value (or vaccineEfficacyPct) and truncate as needed.
  useEffect(() => {
    if (inputs.waningMode !== 'schedule') return;
    const expectedLen = Math.max(0, effectiveHorizon - 1);
    const current = inputs.veSchedule ?? [];
    if (current.length === expectedLen) return;
    const fill = current.length > 0
      ? current[current.length - 1]
      : (inputs.vaccineEfficacyPct ?? 0);
    const next = expectedLen === 0
      ? []
      : current.length > expectedLen
        ? current.slice(0, expectedLen)
        : [...current, ...Array(expectedLen - current.length).fill(fill)];
    actions.updateInput({ veSchedule: next });
  }, [inputs.waningMode, effectiveHorizon, inputs.veSchedule, inputs.vaccineEfficacyPct, actions]);

  // Keep each complication's incidence/cost band arrays aligned with the
  // current bandCount derived from effectiveHorizon. Pad with last value
  // (or 0) and truncate as needed. Mirrors the veSchedule pad/truncate idiom.
  useEffect(() => {
    const list = inputs.complications;
    if (!Array.isArray(list) || list.length === 0) return;
    const cycles = Math.max(0, effectiveHorizon - 1);
    const bandCount = Math.ceil(cycles / 5);
    const align = (arr: number[] | undefined): number[] => {
      const cur = Array.isArray(arr) ? arr : [];
      if (cur.length === bandCount) return cur;
      if (bandCount === 0) return [];
      const fill = cur.length > 0 ? cur[cur.length - 1] : 0;
      return cur.length > bandCount
        ? cur.slice(0, bandCount)
        : [...cur, ...Array(bandCount - cur.length).fill(fill)];
    };
    let changed = false;
    const next = list.map((c) => {
      const inc = align(c.incidencePctBands);
      const cost = align(c.costPerCaseBands);
      if (inc !== c.incidencePctBands || cost !== c.costPerCaseBands) {
        changed = true;
        return { ...c, incidencePctBands: inc, costPerCaseBands: cost };
      }
      return c;
    });
    if (changed) actions.updateInput({ complications: next });
  }, [effectiveHorizon, inputs.complications, actions]);

  const isNationalPopulation = 
    inputs.selectedRegions.includes('finland') && 
    nationalPopulation === getNationalPopulation(populationMode);
  const isNationalCoverage = 
    inputs.selectedRegions.includes('finland') && 
    nationalCoverage === DEFAULT_INPUTS.defaultCoveragePct;

  return (
    <div className="vaccine-demo__card">
      <div className="vaccine-demo__card-content">
        <div
          className="vaccine-demo__card-header"
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={actions.toggle}
        >
          <h2 className="vaccine-demo__card-title">
            <svg
              className="vaccine-demo__card-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
              />
            </svg>
            Model Inputs
          </h2>
          <svg
            className="vaccine-demo__card-icon"
            style={{
              marginLeft: "8px",
              transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
        <div className="vaccine-demo__card-actions">
          <button onClick={actions.reset} className="btn btn-secondary btn-sm">
            Reset
          </button>
        </div>
        {!isCollapsed && (
          <>
            {/* Population & Demographics */}
            <div className="vaccine-demo__input-section">
              <div
                className="vaccine-demo__section-title"
                role="button"
                tabIndex={0}
                aria-expanded={expandedSections.populationDemographics}
                onClick={() => toggleSection('populationDemographics')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection('populationDemographics');
                  }
                }}
              >
                <span>Population & Demographics</span>
                <span className="vaccine-demo__section-title__chevron">
                  {expandedSections.populationDemographics ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.populationDemographics && (
                <>
                  <RegionSelector
                selectedRegions={inputs.selectedRegions}
                onRegionsChange={actions.changeRegions}
              />
              <div className="vaccine-demo__section-header" style={{ marginTop: '16px' }}>
                <label className="vaccine-demo__label">Population mode</label>
                <div className="vaccine-demo__segmented-control">
                  <button
                    type="button"
                    className={`vaccine-demo__segment-btn ${populationMode === 'all' ? 'active' : ''}`}
                    onClick={() => actions.changePopulationMode('all')}
                  >
                    All ages
                  </button>
                  <button
                    type="button"
                    className={`vaccine-demo__segment-btn ${populationMode === 'adult_15_64' ? 'active' : ''}`}
                    onClick={() => actions.changePopulationMode('adult_15_64')}
                  >
                    Ages 15–64
                  </button>
                </div>
              </div>

              {inputs.selectedRegions.length > 1 ? (
                <div className="vaccine-demo__population-display">
                  <div className="vaccine-demo__label">
                    Total population
                    <span className="vaccine-demo__population-subtitle">
                      ({inputs.selectedRegions.length} regions, {populationMode === 'all' ? 'all ages' : '15–64'})
                    </span>
                  </div>
                  <div className="vaccine-demo__population-value-large">
                    {nationalPopulation.toLocaleString('fi-FI')}
                  </div>
                </div>
              ) : (
                <NumberField
                  label={`${isNationalPopulation ? 'National' : 'Total'} population (${populationMode === 'all' ? 'all ages' : '15–64'})`}
                  value={nationalPopulation}
                  onChange={actions.changeNationalPopulation}
                  min={0}
                  error={errors['population']}
                />
              )}
              <div style={{ marginTop: '16px' }}>
                <NumberField
                  label={`${isNationalCoverage ? 'National' : 'Total'} coverage (%)`}
                  value={nationalCoverage}
                  onChange={actions.changeNationalCoverage}
                  min={0}
                  max={100}
                  error={errors['coverage']}
                />
              </div>
                </>
              )}
            </div>

            {/* Disease Epidemiology */}
            <div className="vaccine-demo__input-section">
              <div
                className="vaccine-demo__section-title"
                role="button"
                tabIndex={0}
                aria-expanded={expandedSections.diseaseEpidemiology}
                onClick={() => toggleSection('diseaseEpidemiology')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection('diseaseEpidemiology');
                  }
                }}
              >
                <span>Disease Epidemiology</span>
                <span className="vaccine-demo__section-title__chevron">
                  {expandedSections.diseaseEpidemiology ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.diseaseEpidemiology && (
                <div className="vaccine-demo__input-grid">
                <NumberField
                  label="Baseline incidence (/100k)"
                  value={inputs.baselineIncidencePer100k}
                  onChange={(value) =>
                    actions.updateInput({ baselineIncidencePer100k: value })
                  }
                  error={errors['baselineIncidence']}
                  citation={
                    <CitationField
                      value={inputs.citations?.baselineIncidence}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, baselineIncidence: value }
                      })}
                      placeholder="e.g., WHO Report 2024"
                    />
                  }
                />
                {(() => {
                  const cycles = Math.max(0, effectiveHorizon - 1);
                  const bandCount = Math.ceil(cycles / 5);
                  const scheduleEnabled = cycles > 0;
                  const incidenceMode = inputs.incidenceMode ?? 'scalar';
                  const startingAge = inputs.cohortStartingAge;
                  const bands = inputs.incidenceBandsPer100k ?? [];

                  const switchTo = (mode: 'scalar' | 'schedule') => {
                    if (mode === 'schedule') {
                      let next = bands.slice(0, bandCount);
                      if (next.length < bandCount) {
                        next = next.concat(
                          Array(bandCount - next.length).fill(inputs.baselineIncidencePer100k ?? 0),
                        );
                      }
                      actions.updateInput({ incidenceMode: 'schedule', incidenceBandsPer100k: next });
                    } else {
                      actions.updateInput({ incidenceMode: 'scalar' });
                    }
                  };

                  const bandLabel = (idx: number): string => {
                    if (typeof startingAge !== 'number') return `Band ${idx + 1}`;
                    const lo = startingAge + idx * 5;
                    const lastCycleAge = startingAge + cycles - 1;
                    const hi = Math.min(startingAge + idx * 5 + 4, lastCycleAge);
                    return `Age ${lo}–${hi}`;
                  };

                  return (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="vaccine-demo__label">Incidence input</label>
                      <div style={{ display: 'flex', gap: '16px', margin: '6px 0 10px', flexWrap: 'wrap' }}>
                        {(['scalar', 'schedule'] as const).map((mode) => {
                          const selected = incidenceMode === mode;
                          const label = mode === 'scalar' ? 'Scalar' : 'Variable by age (5-year bands)';
                          return (
                            <label
                              key={mode}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: scheduleEnabled || mode === 'scalar' ? 'pointer' : 'not-allowed',
                                opacity: scheduleEnabled || mode === 'scalar' ? 1 : 0.5,
                                fontSize: '13px',
                              }}
                            >
                              <input
                                type="radio"
                                name="incidenceMode"
                                value={mode}
                                checked={selected}
                                disabled={!scheduleEnabled && mode === 'schedule'}
                                onChange={() => switchTo(mode)}
                                style={{ accentColor: selected ? '#a7d486' : '#4C8DFF' }}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                      </div>
                      {!scheduleEnabled && (
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '-4px', marginBottom: '8px' }}>
                          Variable by age requires a time horizon &gt; 1 year.
                        </div>
                      )}
                      {incidenceMode === 'schedule' && (
                        <>
                          <NumberField
                            label="Cohort starting age"
                            value={startingAge ?? 0}
                            onChange={(value) =>
                              actions.updateInput({ cohortStartingAge: Math.round(value) })
                            }
                            min={0}
                            max={100}
                            step={1}
                            error={errors['cohortStartingAge']}
                          />
                          <div style={{ display: 'flex', gap: '8px', margin: '8px 0', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() =>
                                actions.updateInput({
                                  incidenceBandsPer100k: Array(bandCount).fill(
                                    inputs.baselineIncidencePer100k ?? 0,
                                  ),
                                })
                              }
                            >
                              Reset to baseline
                            </button>
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                              gap: '8px',
                            }}
                          >
                            {Array.from({ length: bandCount }).map((_, idx) => (
                              <NumberField
                                key={idx}
                                label={bandLabel(idx)}
                                value={bands[idx] ?? inputs.baselineIncidencePer100k ?? 0}
                                onChange={(next) => {
                                  const updated = bands.slice(0, bandCount);
                                  while (updated.length < bandCount) {
                                    updated.push(inputs.baselineIncidencePer100k ?? 0);
                                  }
                                  updated[idx] = next;
                                  actions.updateInput({ incidenceBandsPer100k: updated });
                                }}
                                min={0}
                                max={100000}
                                step={0.1}
                                suffix="/100k"
                                compact
                              />
                            ))}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                            Values apply to all cycles falling inside each 5-year age band, starting
                            from the cohort starting age.
                          </div>
                          {errors['incidenceSchedule'] && (
                            <div className="vaccine-demo__error-text" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                              {errors['incidenceSchedule']}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}
                <NumberField
                  label="Duration of infection (days)"
                  value={inputs.durationInfectionDays}
                  onChange={(value) =>
                    actions.updateInput({ durationInfectionDays: value })
                  }
                  error={errors['durationInfection']}
                  citation={
                    <CitationField
                      value={inputs.citations?.durationInfection}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, durationInfection: value }
                      })}
                      placeholder="e.g., Clinical Study 2024"
                    />
                  }
                />
                <NumberField
                  label="Hospitalization rate (%)"
                  value={inputs.hospitalizationRate * 100}
                  onChange={(value) =>
                    actions.updateInput({ hospitalizationRate: value / 100 })
                  }
                  min={0}
                  max={100}
                  error={errors['hospitalizationRate']}
                  citation={
                    <CitationField
                      value={inputs.citations?.hospitalizationRate}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, hospitalizationRate: value }
                      })}
                      placeholder="e.g., Hospital Data 2024"
                    />
                  }
                />
                <NumberField
                  label="Hospitalization cost/case (€)"
                  value={inputs.hospitalizationCostPerCaseEUR}
                  onChange={(value) =>
                    actions.updateInput({ hospitalizationCostPerCaseEUR: value })
                  }
                  min={0}
                  max={100000}
                  error={errors['hospitalizationCost']}
                  citation={
                    <CitationField
                      value={inputs.citations?.hospitalizationCost}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, hospitalizationCost: value }
                      })}
                      placeholder="e.g., Hospital Cost Study 2024"
                    />
                  }
                />
                <NumberField
                  label="Hospitalization duration (days)"
                  value={inputs.durationHospitalizationDays}
                  onChange={(value) =>
                    actions.updateInput({ durationHospitalizationDays: value })
                  }
                  min={0}
                  max={365}
                  error={errors['durationHospitalization']}
                  citation={
                    <CitationField
                      value={inputs.citations?.durationHospitalization}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, durationHospitalization: value }
                      })}
                      placeholder="e.g., Clinical Study 2024"
                    />
                  }
                />
                <NumberField
                  label="Utility during hospitalization (0-1)"
                  value={inputs.utilityHospitalization}
                  onChange={(value) =>
                    actions.updateInput({ utilityHospitalization: value })
                  }
                  min={0}
                  max={1}
                  step={0.01}
                  error={errors['utilityHospitalization']}
                />
                <NumberField
                  label="Infection-related mortality (%)"
                  helpText="Excess mortality rate among infected individuals (not background all-cause mortality)"
                  value={inputs.baselineMortalityRate * 100}
                  onChange={(value) =>
                    actions.updateInput({ baselineMortalityRate: value / 100 })
                  }
                  min={0}
                  max={100}
                  step={0.001}
                  error={errors['mortalityRate']}
                  citation={
                    <CitationField
                      value={inputs.citations?.mortalityRate}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, mortalityRate: value }
                      })}
                      placeholder="e.g., Mortality Study 2024"
                    />
                  }
                />
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="vaccine-demo__label">Background mortality</label>
                  <div style={{ display: 'flex', gap: '16px', margin: '6px 0 10px', flexWrap: 'wrap' }}>
                    {(['none', 'finnish_lifetable_2023'] as const).map((mode) => {
                      const selected = (inputs.mortalitySource ?? 'none') === mode;
                      const label =
                        mode === 'none'
                          ? 'None'
                          : 'Finnish life table (Tilastokeskus 2023)';
                      return (
                        <label
                          key={mode}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          <input
                            type="radio"
                            name="mortalitySource"
                            value={mode}
                            checked={selected}
                            onChange={() => actions.updateInput({ mortalitySource: mode })}
                            style={{ accentColor: selected ? '#a7d486' : '#4C8DFF' }}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {inputs.mortalitySource === 'finnish_lifetable_2023' && (
                    <>
                      <NumberField
                        label="Cohort starting age"
                        value={inputs.cohortStartingAge ?? 0}
                        onChange={(value) =>
                          actions.updateInput({ cohortStartingAge: Math.round(value) })
                        }
                        min={0}
                        max={100}
                        step={1}
                        error={errors['cohortStartingAge']}
                      />
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                        Applies per-cycle background all-cause mortality resolved server-side
                        from Tilastokeskus 2023. Separate from the case-fatality rate above.
                      </div>
                    </>
                  )}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="vaccine-demo__label">Background utility (healthy-state HRQoL)</label>
                  <div style={{ display: 'flex', gap: '16px', margin: '6px 0 10px', flexWrap: 'wrap' }}>
                    {BACKGROUND_UTILITY_SOURCE_OPTIONS.map((opt) => {
                      const selected = (inputs.backgroundUtilitySource ?? 'none') === opt.id;
                      return (
                        <label
                          key={opt.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          <input
                            type="radio"
                            name="backgroundUtilitySource"
                            value={opt.id}
                            checked={selected}
                            onChange={() => actions.updateInput({ backgroundUtilitySource: opt.id })}
                            style={{ accentColor: selected ? '#a7d486' : '#4C8DFF' }}
                          />
                          <span>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {(() => {
                    const preset = findBackgroundUtilitySource(inputs.backgroundUtilitySource);
                    if (!preset) return null;
                    const age = inputs.cohortStartingAge;
                    const needsAge = typeof age !== 'number';
                    return (
                      <>
                        {needsAge && (
                          <NumberField
                            label="Cohort starting age"
                            value={inputs.cohortStartingAge ?? 0}
                            onChange={(value) =>
                              actions.updateInput({ cohortStartingAge: Math.round(value) })
                            }
                            min={0}
                            max={100}
                            step={1}
                            error={errors['cohortStartingAge']}
                          />
                        )}
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                          Applies per-cycle population HRQoL resolved server-side from THL
                          Terveys 2011. Multiplies QALYs in both arms; incremental effect is
                          small but non-zero for long-horizon cohorts.
                        </div>
                        <details
                          style={{
                            marginTop: '6px',
                            padding: '8px 10px',
                            background: 'var(--easyhta-light-surface)',
                            border: '1px solid var(--white-10)',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: 'var(--easyhta-light-text-secondary)',
                          }}
                        >
                          <summary
                            style={{
                              cursor: 'pointer',
                              color: 'var(--easyhta-light-text)',
                              fontWeight: 500,
                            }}
                          >
                            Show per-cycle schedule details
                          </summary>
                          <div style={{ marginTop: '6px', lineHeight: 1.5 }}>
                            <div>
                              <strong>Source:</strong> {preset.label}
                            </div>
                            <div>
                              <strong>Value type:</strong> {preset.valueType}
                            </div>
                            <div>
                              <strong>Age range covered:</strong> {preset.ageRange[0]}–
                              {preset.ageRange[1]} (ages past the terminal pad with
                              the terminal-age value)
                            </div>
                            <div>
                              <strong>Bands:</strong> {preset.summary}
                            </div>
                            {preset.note && (
                              <div style={{ marginTop: '6px' }}>{preset.note}</div>
                            )}
                          </div>
                        </details>
                      </>
                    );
                  })()}
                </div>
                {(() => {
                  const cycles = Math.max(0, effectiveHorizon - 1);
                  const bandCount = Math.ceil(cycles / 5);
                  const scheduleEnabled = cycles > 0;
                  const qalyLossMode = inputs.qalyLossMode ?? 'scalar';
                  const startingAge = inputs.cohortStartingAge;
                  const bands = inputs.qalyLossBands ?? [];

                  const switchTo = (mode: 'scalar' | 'schedule') => {
                    if (mode === 'schedule') {
                      let next = bands.slice(0, bandCount);
                      if (next.length < bandCount) {
                        next = next.concat(Array(bandCount - next.length).fill(0));
                      }
                      actions.updateInput({ qalyLossMode: 'schedule', qalyLossBands: next });
                    } else {
                      actions.updateInput({ qalyLossMode: 'scalar' });
                    }
                  };

                  const bandLabel = (idx: number): string => {
                    if (typeof startingAge !== 'number') return `Band ${idx + 1}`;
                    const lo = startingAge + idx * 5;
                    const lastCycleAge = startingAge + cycles - 1;
                    const hi = Math.min(lo + 4, lastCycleAge);
                    return `Age ${lo}–${hi}`;
                  };

                  return (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="vaccine-demo__label">Per-case QALY loss</label>
                      <div style={{ display: 'flex', gap: '16px', margin: '6px 0 10px', flexWrap: 'wrap' }}>
                        {(['scalar', 'schedule'] as const).map((mode) => {
                          const selected = qalyLossMode === mode;
                          const label = mode === 'scalar' ? 'Scalar (utility × duration)' : 'Variable by age (5-year bands)';
                          return (
                            <label
                              key={mode}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: scheduleEnabled || mode === 'scalar' ? 'pointer' : 'not-allowed',
                                opacity: scheduleEnabled || mode === 'scalar' ? 1 : 0.5,
                                fontSize: '13px',
                              }}
                            >
                              <input
                                type="radio"
                                name="qalyLossMode"
                                value={mode}
                                checked={selected}
                                disabled={!scheduleEnabled && mode === 'schedule'}
                                onChange={() => switchTo(mode)}
                                style={{ accentColor: selected ? '#a7d486' : '#4C8DFF' }}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                      </div>
                      {!scheduleEnabled && (
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '-4px', marginBottom: '8px' }}>
                          Variable by age requires a time horizon &gt; 1 year.
                        </div>
                      )}
                      {qalyLossMode === 'schedule' && (
                        <>
                          {typeof startingAge !== 'number' && (
                            <NumberField
                              label="Cohort starting age"
                              value={inputs.cohortStartingAge ?? 0}
                              onChange={(v) => actions.updateInput({ cohortStartingAge: Math.round(v) })}
                              min={0}
                              max={100}
                              step={1}
                              error={errors['cohortStartingAge']}
                            />
                          )}
                          <div style={{ display: 'flex', gap: '8px', margin: '8px 0', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => actions.updateInput({ qalyLossBands: Array(bandCount).fill(0) })}
                            >
                              Reset to 0
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px' }}>
                            {Array.from({ length: bandCount }).map((_, idx) => (
                              <NumberField
                                key={idx}
                                label={bandLabel(idx)}
                                value={bands[idx] ?? 0}
                                onChange={(next) => {
                                  const updated = bands.slice(0, bandCount);
                                  while (updated.length < bandCount) updated.push(0);
                                  updated[idx] = next;
                                  actions.updateInput({ qalyLossBands: updated });
                                }}
                                min={0}
                                max={1}
                                step={0.001}
                                compact
                              />
                            ))}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                            QALY loss per infection case per 5-year age band. Duration input is still used
                            to convert these to per-cycle values.
                          </div>
                          {errors['qalyLossSchedule'] && (
                            <div className="vaccine-demo__error-text" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                              {errors['qalyLossSchedule']}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}
                <NumberField
                  label="Utility during infection (0-1)"
                  value={inputs.utilityInfection}
                  onChange={(value) =>
                    actions.updateInput({ utilityInfection: value })
                  }
                  min={0}
                  max={1}
                  step={0.01}
                  disabled={(inputs.qalyLossMode ?? 'scalar') !== 'scalar'}
                  error={errors['utilityInfection']}
                  citation={
                    <CitationField
                      value={inputs.citations?.utilityInfection}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, utilityInfection: value }
                      })}
                      placeholder="e.g., Health Utility Study 2024"
                    />
                  }
                />
              </div>
              )}
            </div>

            {/* Vaccine Characteristics */}
            <div className="vaccine-demo__input-section">
              <div
                className="vaccine-demo__section-title"
                role="button"
                tabIndex={0}
                aria-expanded={expandedSections.vaccineCharacteristics}
                onClick={() => toggleSection('vaccineCharacteristics')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection('vaccineCharacteristics');
                  }
                }}
              >
                <span>Vaccine Characteristics</span>
                <span className="vaccine-demo__section-title__chevron">
                  {expandedSections.vaccineCharacteristics ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.vaccineCharacteristics && (
                <div className="vaccine-demo__input-grid">
                <NumberField
                  label="Efficacy (%)"
                  value={inputs.vaccineEfficacyPct}
                  onChange={(value) =>
                    actions.updateInput({ vaccineEfficacyPct: value })
                  }
                  error={errors['vaccineEfficacy']}
                  citation={
                    <CitationField
                      value={inputs.citations?.vaccineEfficacy}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, vaccineEfficacy: value }
                      })}
                      placeholder="e.g., Clinical Trial ABC, 2023"
                    />
                  }
                />
                <NumberField
                  label="Doses/course"
                  value={inputs.dosesPerCourse}
                  onChange={(value) =>
                    actions.updateInput({ dosesPerCourse: value })
                  }
                  error={errors['dosesPerCourse']}
                />
                <NumberField
                  label="Vaccine cost/dose (€)"
                  value={inputs.vaccineCostPerDoseEUR}
                  onChange={(value) =>
                    actions.updateInput({ vaccineCostPerDoseEUR: value })
                  }
                  error={errors['vaccineCost']}
                  citation={
                    <CitationField
                      value={inputs.citations?.vaccineCost}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, vaccineCost: value }
                      })}
                      placeholder="e.g., Manufacturer Price List 2024"
                    />
                  }
                />
                <NumberField
                  label="Vaccine price discount (%)"
                  value={inputs.vaccinePriceDiscountPct ?? 0}
                  onChange={(value) => {
                    const clampedValue = Math.max(0, Math.min(100, value));
                    actions.updateInput({ vaccinePriceDiscountPct: clampedValue });
                  }}
                  min={0}
                  max={100}
                  helpText={
                    inputs.vaccineCostPerDoseEUR > 0 && inputs.vaccinePriceDiscountPct > 0
                      ? `Effective price: ${(inputs.vaccineCostPerDoseEUR * (1 - (inputs.vaccinePriceDiscountPct ?? 0) / 100)).toFixed(2)} €`
                      : undefined
                  }
                  error={errors['vaccinePriceDiscount']}
                  aria-label="Vaccine price discount percentage"
                />
                <NumberField
                  label="Admin cost/dose (€)"
                  value={inputs.adminCostPerDoseEUR}
                  onChange={(value) =>
                    actions.updateInput({ adminCostPerDoseEUR: value })
                  }
                  error={errors['adminCost']}
                  citation={
                    <CitationField
                      value={inputs.citations?.adminCost}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, adminCost: value }
                      })}
                      placeholder="e.g., Healthcare Cost Report 2024"
                    />
                  }
                />
              </div>
              )}
            </div>

            {/* Adverse Events */}
            <div className="vaccine-demo__input-section">
              <div
                className="vaccine-demo__section-title"
                role="button"
                tabIndex={0}
                aria-expanded={expandedSections.adverseEvents}
                onClick={() => toggleSection('adverseEvents')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection('adverseEvents');
                  }
                }}
              >
                <span>Adverse Events</span>
                <span className="vaccine-demo__section-title__chevron">
                  {expandedSections.adverseEvents ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.adverseEvents && (
                <div className="vaccine-demo__input-grid">
                <NumberField
                  label="Adverse event rate (0-1)"
                  value={inputs.adverseEventRate}
                  onChange={(value) =>
                    actions.updateInput({ adverseEventRate: value })
                  }
                  min={0}
                  max={1}
                  step={0.001}
                  error={errors['adverseEventRate']}
                  citation={
                    <CitationField
                      value={inputs.citations?.adverseEventRate}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, adverseEventRate: value }
                      })}
                      placeholder="e.g., Safety Study 2024"
                    />
                  }
                />
                <NumberField
                  label="Adverse event cost (€)"
                  value={inputs.adverseEventCost}
                  onChange={(value) =>
                    actions.updateInput({ adverseEventCost: value })
                  }
                  min={0}
                  step={1}
                  error={errors['adverseEventCost']}
                  citation={
                    <CitationField
                      value={inputs.citations?.adverseEventCost}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, adverseEventCost: value }
                      })}
                      placeholder="e.g., Cost Analysis 2024"
                    />
                  }
                />
                <NumberField
                  label="Adverse event QALY loss (0-1)"
                  value={inputs.adverseEventQalyLoss}
                  onChange={(value) =>
                    actions.updateInput({ adverseEventQalyLoss: value })
                  }
                  min={0}
                  max={1}
                  step={0.001}
                  error={errors['adverseEventQalyLoss']}
                  citation={
                    <CitationField
                      value={inputs.citations?.adverseEventQalyLoss}
                      onChange={(value) => actions.updateInput({
                        citations: { ...inputs.citations, adverseEventQalyLoss: value }
                      })}
                      placeholder="e.g., QALY Impact Study 2024"
                    />
                  }
                />
              </div>
              )}
            </div>

            {/* Economic Parameters */}
            <div className="vaccine-demo__input-section">
              <div
                className="vaccine-demo__section-title"
                role="button"
                tabIndex={0}
                aria-expanded={expandedSections.economicParameters}
                onClick={() => toggleSection('economicParameters')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection('economicParameters');
                  }
                }}
              >
                <span>Economic Parameters</span>
                <span className="vaccine-demo__section-title__chevron">
                  {expandedSections.economicParameters ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.economicParameters && (
                <div className="vaccine-demo__input-grid">
                  <NumberField
                    label="Treatment cost/case (€)"
                    value={inputs.treatmentCostPerCaseEUR}
                    onChange={(value) =>
                      actions.updateInput({ treatmentCostPerCaseEUR: value })
                    }
                    error={errors['treatmentCost']}
                    citation={
                      <CitationField
                        value={inputs.citations?.treatmentCost}
                        onChange={(value) => actions.updateInput({
                          citations: { ...inputs.citations, treatmentCost: value }
                        })}
                        placeholder="e.g., Hospital Cost Study 2023"
                      />
                    }
                  />
                </div>
              )}
            </div>

            {/* Complications */}
            <div className="vaccine-demo__input-section">
              <div
                className="vaccine-demo__section-title"
                role="button"
                tabIndex={0}
                aria-expanded={expandedSections.complications}
                onClick={() => toggleSection('complications')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection('complications');
                  }
                }}
              >
                <span>Complications</span>
                <span className="vaccine-demo__section-title__chevron">
                  {expandedSections.complications ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.complications && (() => {
                const cycles = Math.max(0, effectiveHorizon - 1);
                const bandCount = Math.ceil(cycles / 5);
                const startingAge = inputs.cohortStartingAge;
                const list = inputs.complications ?? [];

                const compBandLabel = (idx: number): string => {
                  if (typeof startingAge !== 'number') return `Band ${idx + 1}`;
                  const lo = startingAge + idx * 5;
                  const lastCycleAge = startingAge + cycles - 1;
                  const hi = Math.min(lo + 4, lastCycleAge);
                  return `Age ${lo}–${hi}`;
                };

                const newId = (): string =>
                  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `comp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

                const updateAt = (idx: number, patch: Partial<ComplicationCategory>) => {
                  const next = list.map((c, i) => (i === idx ? { ...c, ...patch } : c));
                  actions.updateInput({ complications: next });
                };

                const removeAt = (idx: number) => {
                  actions.updateInput({ complications: list.filter((_, i) => i !== idx) });
                };

                const addOne = () => {
                  const next: ComplicationCategory = {
                    id: newId(),
                    name: '',
                    enabled: true,
                    incidencePctBands: Array(bandCount).fill(0),
                    costPerCaseBands: Array(bandCount).fill(0),
                  };
                  actions.updateInput({ complications: [...list, next] });
                };

                const totalIncidenceByBand: number[] = Array.from({ length: bandCount }, (_, b) =>
                  list
                    .filter((c) => c.enabled !== false)
                    .reduce((acc, c) => acc + (c.incidencePctBands?.[b] ?? 0), 0),
                );
                const anyOver100 = totalIncidenceByBand.some((v) => v > 100);

                return (
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--vaccine-text-muted)', marginBottom: '12px' }}>
                      Add post-infection complications. Each is multiplied per cycle by p_inf
                      (probability of infection) to add to total cost. Patients can have more than
                      one complication, so per-band totals may exceed 100%.
                    </div>

                    {list.length === 0 ? (
                      <div
                        style={{
                          padding: '20px 8px',
                          textAlign: 'center',
                          color: 'var(--vaccine-text-muted)',
                        }}
                      >
                        <div style={{ fontSize: 'var(--font-size-base)', marginBottom: '12px' }}>
                          No complications configured. Click <em>Add complication</em> to start.
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {list.map((comp, idx) => {
                          const inc = comp.incidencePctBands ?? [];
                          const cost = comp.costPerCaseBands ?? [];
                          const isOff = comp.enabled === false;
                          return (
                            <div
                              key={comp.id}
                              style={{
                                border: '1px solid var(--vaccine-border)',
                                borderRadius: '6px',
                                padding: '12px',
                                background: 'var(--vaccine-bg-panel)',
                                opacity: isOff ? 0.6 : 1,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                  marginBottom: '10px',
                                  flexWrap: 'wrap',
                                }}
                              >
                                <input
                                  type="text"
                                  value={comp.name}
                                  onChange={(e) => updateAt(idx, { name: e.target.value })}
                                  placeholder="Name this complication"
                                  aria-label={`Complication ${idx + 1} name`}
                                  style={{
                                    flex: '1 1 200px',
                                    minWidth: '160px',
                                    padding: '6px 8px',
                                    border: '1px solid var(--vaccine-border)',
                                    borderRadius: '4px',
                                    background: 'var(--vaccine-bg-panel-hover)',
                                    color: 'var(--vaccine-text-main)',
                                    fontSize: '13px',
                                  }}
                                />
                                <label
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={comp.enabled !== false}
                                    onChange={(e) => updateAt(idx, { enabled: e.target.checked })}
                                    style={{ accentColor: '#a7d486' }}
                                  />
                                  <span>Enabled</span>
                                </label>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => removeAt(idx)}
                                  aria-label={`Remove ${comp.name}`}
                                >
                                  Remove
                                </button>
                              </div>

                              {bandCount === 0 ? (
                                <div style={{ fontSize: '12px', color: 'var(--vaccine-text-muted)' }}>
                                  Time horizon must be &gt; 1 year to enter age-banded values.
                                </div>
                              ) : (
                                <>
                                  <label className="vaccine-demo__label" style={{ fontSize: '12px' }}>
                                    Incidence (% of infected)
                                  </label>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                      gap: '8px',
                                      margin: '4px 0 10px',
                                    }}
                                  >
                                    {Array.from({ length: bandCount }).map((_, b) => (
                                      <NumberField
                                        key={b}
                                        label={compBandLabel(b)}
                                        value={inc[b] ?? 0}
                                        onChange={(next) => {
                                          const updated = inc.slice(0, bandCount);
                                          while (updated.length < bandCount) updated.push(0);
                                          updated[b] = next;
                                          updateAt(idx, { incidencePctBands: updated });
                                        }}
                                        min={0}
                                        max={100}
                                        step={0.1}
                                        suffix="%"
                                        compact
                                      />
                                    ))}
                                  </div>

                                  <label className="vaccine-demo__label" style={{ fontSize: '12px' }}>
                                    Cost per case (€)
                                  </label>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                      gap: '8px',
                                      margin: '4px 0 8px',
                                    }}
                                  >
                                    {Array.from({ length: bandCount }).map((_, b) => (
                                      <NumberField
                                        key={b}
                                        label={compBandLabel(b)}
                                        value={cost[b] ?? 0}
                                        onChange={(next) => {
                                          const updated = cost.slice(0, bandCount);
                                          while (updated.length < bandCount) updated.push(0);
                                          updated[b] = next;
                                          updateAt(idx, { costPerCaseBands: updated });
                                        }}
                                        min={0}
                                        max={1_000_000}
                                        step={1}
                                        suffix="€"
                                        compact
                                      />
                                    ))}
                                  </div>
                                </>
                              )}

                              <CitationField
                                value={comp.citation}
                                onChange={(v) => updateAt(idx, { citation: v })}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {anyOver100 && (
                      <div
                        style={{
                          marginTop: '10px',
                          fontSize: '12px',
                          color: 'var(--vaccine-text-muted)',
                        }}
                      >
                        Note: total incidence across enabled complications exceeds 100% in at
                        least one band. That is allowed (a patient can have multiple
                        complications) — confirm this matches your modeling intent.
                      </div>
                    )}

                    <div style={{ marginTop: '12px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={addOne}
                      >
                        + Add complication
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Multi-Year Analysis */}
            <div className="vaccine-demo__input-section">
              <div
                className="vaccine-demo__section-title"
                role="button"
                tabIndex={0}
                aria-expanded={expandedSections.multiYearAnalysis}
                onClick={() => toggleSection('multiYearAnalysis')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection('multiYearAnalysis');
                  }
                }}
              >
                <span>Multi-Year Analysis</span>
                <span className="vaccine-demo__section-title__chevron">
                  {expandedSections.multiYearAnalysis ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.multiYearAnalysis && (
                <div className="vaccine-demo__input-grid">
                  <NumberField
                    label="Time horizon (years)"
                    value={horizonMode === 'lifetime' ? effectiveHorizon : inputs.timeHorizonYears}
                    onChange={(value) =>
                      actions.updateInput({ timeHorizonYears: value })
                    }
                    min={1}
                    max={100}
                    disabled={horizonMode === 'lifetime'}
                    helpText={
                      horizonMode === 'lifetime'
                        ? `Auto-derived from cohort starting age to life-table terminal age. Currently ${effectiveHorizon} years.`
                        : undefined
                    }
                    error={errors['timeHorizon']}
                  />
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="vaccine-demo__label">Horizon mode</label>
                    <div style={{ display: 'flex', gap: '16px', margin: '6px 0 10px' }}>
                      {(['manual', 'lifetime'] as const).map((mode) => {
                        const selected = horizonMode === mode;
                        const modeDisabled = mode === 'lifetime' && !canUseLifetime;
                        const label = mode === 'manual' ? 'Manual' : 'Cohort lifetime';
                        return (
                          <label
                            key={mode}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: modeDisabled ? 'not-allowed' : 'pointer',
                              opacity: modeDisabled ? 0.5 : 1,
                              fontSize: '13px',
                            }}
                          >
                            <input
                              type="radio"
                              name="horizonMode"
                              value={mode}
                              checked={selected}
                              disabled={modeDisabled}
                              onChange={() => actions.updateInput({ horizonMode: mode })}
                              style={{ accentColor: selected ? '#a7d486' : '#4C8DFF' }}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                      Manual: use the integer above. Cohort lifetime: derive horizon as
                      (life-table terminal age − starting age + 1). Requires a life-table
                      mortality source and a cohort starting age.
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="vaccine-demo__label">Waning model</label>
                    <div style={{ display: 'flex', gap: '16px', margin: '6px 0 10px' }}>
                      {(['exponential', 'schedule'] as const).map((mode) => {
                        const selected = (inputs.waningMode ?? 'exponential') === mode;
                        const label = mode === 'exponential' ? 'Exponential' : 'Custom schedule';
                        return (
                          <label
                            key={mode}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: effectiveHorizon > 1 ? 'pointer' : 'not-allowed',
                              opacity: effectiveHorizon > 1 ? 1 : 0.5,
                              fontSize: '13px',
                            }}
                          >
                            <input
                              type="radio"
                              name="waningMode"
                              value={mode}
                              checked={selected}
                              disabled={effectiveHorizon <= 1}
                              onChange={() => actions.updateInput({ waningMode: mode })}
                              style={{ accentColor: selected ? '#a7d486' : '#4C8DFF' }}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                      Exponential: single annual decay rate. Custom schedule: per-year VE values
                      (e.g., to reproduce a published piecewise curve).
                    </div>
                  </div>
                  {(inputs.waningMode ?? 'exponential') === 'exponential' ? (
                    <NumberField
                      label="Waning per year (%)"
                      value={inputs.waningPctPerYear}
                      onChange={(value) =>
                        actions.updateInput({ waningPctPerYear: value })
                      }
                      helpText="Annual percentage reduction in vaccine efficacy. Only applies when time horizon > 1 year."
                      disabled={effectiveHorizon <= 1}
                      citation={
                        <CitationField
                          value={inputs.citations?.waning}
                          onChange={(value) => actions.updateInput({
                            citations: { ...inputs.citations, waning: value }
                          })}
                          placeholder="e.g., Waning Study 2024"
                        />
                      }
                    />
                  ) : (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={effectiveHorizon <= 1}
                          onClick={() =>
                            actions.updateInput({
                              veSchedule: Array(
                                Math.max(0, effectiveHorizon - 1),
                              ).fill(inputs.vaccineEfficacyPct),
                            })
                          }
                        >
                          Reset to initial VE
                        </button>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                          gap: '8px',
                        }}
                      >
                        {(inputs.veSchedule ?? []).map((veValue, idx) => {
                          const year = idx + 2;
                          return (
                            <NumberField
                              key={year}
                              label={`Year ${year}`}
                              value={veValue}
                              onChange={(next) => {
                                const updated = [...(inputs.veSchedule ?? [])];
                                updated[idx] = next;
                                actions.updateInput({ veSchedule: updated });
                              }}
                              min={0}
                              max={100}
                              step={0.1}
                              suffix="%"
                              compact
                            />
                          );
                        })}
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                        VE at mid-year for years 2..{effectiveHorizon}. Year 1 uses the
                        initial VE above.
                      </div>
                      {errors['veSchedule'] && (
                        <div className="vaccine-demo__error-text" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                          {errors['veSchedule']}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <NumberField
                      label="Discount rate (%)"
                      value={inputs.discountRatePct}
                      onChange={(value) =>
                        actions.updateInput({ discountRatePct: value })
                      }
                      min={0}
                      max={100}
                      error={errors['discountRate']}
                      disabled={effectiveHorizon <= 1}
                      warning={
                        inputs.discountRatePct > 5 && inputs.discountRatePct <= 100
                          ? 'Warning: Discount rate over 5% is unusually high. Typical discount rates are 3-5% per year.'
                          : undefined
                      }
                    />
                    <div className="vaccine-demo__citation-wrapper" style={{ marginTop: '2px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: effectiveHorizon > 1 ? 'pointer' : 'not-allowed', opacity: effectiveHorizon > 1 ? 1 : 0.5, fontSize: '11px', color: '#64748b' }}>
                        <input
                          type="checkbox"
                          checked={inputs.discountingEnabled ?? false}
                          onChange={(e) => {
                            if (effectiveHorizon > 1) {
                              actions.updateInput({ discountingEnabled: Boolean(e.target.checked) });
                            }
                          }}
                          disabled={effectiveHorizon <= 1}
                          style={{ accentColor: inputs.discountingEnabled ? '#a7d486' : '#4C8DFF', cursor: effectiveHorizon > 1 ? 'pointer' : 'not-allowed' }}
                        />
                        <span style={{ whiteSpace: 'nowrap' }}>
                          Enable discounting
                          {effectiveHorizon <= 1 && (
                            <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '4px' }}>
                              ({'>'}1 year)
                            </span>
                          )}
                        </span>
                      </label>
                    </div>
                  </div>
                  {effectiveHorizon > 1 && (
                    <>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label className="vaccine-demo__label">
                          Booster schedule
                        </label>
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                            {Array.from({ length: Math.max(0, effectiveHorizon - 1) }, (_, i) => {
                              const year = i + 2;
                              const isSelected = (inputs.boosterYears || []).includes(year);
                              return (
                                <label
                                  key={year}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    cursor: effectiveHorizon > 1 ? 'pointer' : 'not-allowed',
                                    opacity: effectiveHorizon > 1 ? 1 : 0.5,
                                    fontSize: '13px',
                                    padding: '6px 12px',
                                    border: `1px solid ${isSelected ? '#a7d486' : '#475569'}`,
                                    borderRadius: '6px',
                                    backgroundColor: isSelected ? 'rgba(167, 212, 134, 0.2)' : 'transparent',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (effectiveHorizon > 1) {
                                        const currentYears = inputs.boosterYears || [];
                                        const newYears = e.target.checked
                                          ? [...currentYears, year].sort((a, b) => a - b)
                                          : currentYears.filter((y) => y !== year);
                                        actions.updateInput({ boosterYears: newYears });
                                      }
                                    }}
                                    disabled={effectiveHorizon <= 1}
                                    style={{ accentColor: isSelected ? '#a7d486' : '#4C8DFF', cursor: effectiveHorizon > 1 ? 'pointer' : 'not-allowed' }}
                                  />
                                  <span>Year {year}</span>
                                </label>
                              );
                            })}
                          </div>
                          {(inputs.boosterYears || []).length > 0 && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                              {(inputs.boosterYears || []).length} year{(inputs.boosterYears || []).length !== 1 ? 's' : ''} selected
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                            Select years when booster doses are administered. Boosters reset vaccine efficacy to initial value.
                          </div>
                          {errors['boosterYears'] && (
                            <div className="vaccine-demo__error-text" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                              {errors['boosterYears']}
                            </div>
                          )}
                        </div>
                      </div>
                      <NumberField
                        label="Booster cost per dose (€)"
                        value={inputs.boosterCost}
                        onChange={(value) =>
                          actions.updateInput({ boosterCost: value })
                        }
                        min={0}
                        disabled={effectiveHorizon <= 1}
                        helpText="Cost per booster dose administered"
                        error={errors['boosterCost']}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Productivity Costs */}
            <div className="vaccine-demo__input-section">
              <div className="vaccine-demo__section-header">
                <div className="vaccine-demo__section-title" style={{ marginBottom: 0 }}>Productivity Costs</div>
                <div className="vaccine-demo__segmented-control">
                  <button
                    type="button"
                    className={`vaccine-demo__segment-btn ${inputs.perspective === 'healthcare' ? 'active' : ''}`}
                    onClick={() => actions.updateInput({ perspective: 'healthcare' })}
                  >
                    Healthcare
                  </button>
                  <button
                    type="button"
                    className={`vaccine-demo__segment-btn ${inputs.perspective === 'societal' ? 'active' : ''}`}
                    onClick={() => actions.updateInput({ perspective: 'societal' })}
                  >
                    Societal
                  </button>
                </div>
              </div>
              <div className="vaccine-demo__population-subtitle" style={{ marginLeft: 0, marginTop: '8px', marginBottom: '12px' }}>
                {inputs.perspective === 'societal' 
                  ? 'Includes productivity losses due to illness and death.' 
                  : 'Direct medical costs only (payer perspective).'}
              </div>
              {inputs.perspective === 'societal' && (
                <div className="vaccine-demo__input-grid">
                  <NumberField
                    label="Productivity loss/case (days)"
                    value={inputs.productivityLossDaysPerCase}
                    onChange={(value) =>
                      actions.updateInput({ productivityLossDaysPerCase: value })
                    }
                    error={errors['productivityLossDays']}
                  />
                  <NumberField
                    label="Productivity value/day (€)"
                    value={inputs.productivityValuePerDayEUR}
                    onChange={(value) =>
                      actions.updateInput({ productivityValuePerDayEUR: value })
                    }
                    error={errors['productivityValue']}
                    citation={
                      <CitationField
                        value={inputs.citations?.productivityValue}
                        onChange={(value) => actions.updateInput({
                          citations: { ...inputs.citations, productivityValue: value }
                        })}
                        placeholder="e.g., Labor Statistics 2024"
                      />
                    }
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
